import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

/**
 * Builds an RTK Query `retry` condition that does not retry client errors.
 *
 * A 4xx answer is the same on every attempt — a 403 for a role the endpoint
 * refuses, a 401 for a wrong one-time code — so retrying it only delays the
 * message by the backoff (and, for a code, spends attempts against the
 * server's limiter). 408 and 429 are transient and are still retried, as is
 * every other error, up to `maxRetries`.
 *
 * Pass the result as an endpoint's `extraOptions.retryCondition`.
 */
export function retryUnlessClientError(maxRetries: number) {
  return (
    error: FetchBaseQueryError,
    _args: unknown,
    { attempt }: { attempt: number },
  ): boolean => {
    const { status } = error;
    const transient = status === 408 || status === 429;
    if (typeof status === "number" && status >= 400 && status < 500 && !transient) {
      return false;
    }
    return attempt <= maxRetries;
  };
}

export default retryUnlessClientError;
